/**
 * Tiny observable store - no framework, no dependencies.
 * Components re-render from `render.ts` whenever the state changes.
 */

import type { AuditReport } from '../utils/image-auditor.ts';
import type { ExportFormat, MarketplaceId } from '../utils/batch-exporter.ts';

export interface ProductImage {
  id: string;
  fileName: string;
  /** Decoded original pixels (never mutated - used by "Reset") */
  originalPixels: Uint8ClampedArray;
  originalWidth: number;
  originalHeight: number;
  /** Current pixels after any applied repairs */
  workingPixels: Uint8ClampedArray;
  workingWidth: number;
  workingHeight: number;
  report: AuditReport;
  snapped: boolean;
  autoFilled: boolean;
}

export type ToastKind = 'info' | 'success' | 'error';

export interface AppState {
  images: ProductImage[];
  /** Image shown in the mobile grid simulator */
  activeId: string | null;
  simTheme: 'light' | 'dark';
  /** 0-based slot the user's listing occupies in the 6-card grid */
  simSlot: number;
  isPro: boolean;
  licenseInput: string;
  licenseMessage: string | null;
  licenseValid: boolean;
  preset: MarketplaceId;
  format: ExportFormat;
  quality: number;
  sku: string;
  exporting: boolean;
  toast: { kind: ToastKind; text: string } | null;
}

export const initialState: AppState = {
  images: [],
  activeId: null,
  simTheme: 'light',
  simSlot: 0,
  isPro: false,
  licenseInput: '',
  licenseMessage: null,
  licenseValid: false,
  preset: 'amazon',
  format: 'image/jpeg',
  quality: 0.92,
  sku: 'ceramic-vase',
  exporting: false,
  toast: null
};

export type Listener = (state: AppState) => void;

export class Store {
  private state: AppState = { ...initialState };
  private listeners: Set<Listener> = new Set();
  private imageCounter = 0;

  getState(): AppState {
    return this.state;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Shallow-merges a patch and notifies subscribers. */
  set(patch: Partial<AppState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener(this.state);
  }

  /**
   * Updates state WITHOUT re-rendering - used by text inputs (SKU, license key,
   * quality slider) so the focused element is never replaced mid-typing.
   * The calling code updates any dependent labels directly in the DOM.
   */
  setSilent(patch: Partial<AppState>): void {
    this.state = { ...this.state, ...patch };
  }

  /** Mutates the images array immutably (the array identity changes, items stay shared). */
  updateImages(updater: (images: ProductImage[]) => ProductImage[]): void {
    this.set({ images: updater(this.state.images) });
  }

  nextImageId(): string {
    this.imageCounter += 1;
    return `img-${this.imageCounter}`;
  }

  getActive(): ProductImage | null {
    const { images, activeId } = this.state;
    if (images.length === 0) return null;
    return images.find((image) => image.id === activeId) ?? images[0];
  }

  clear(): void {
    this.set({
      images: [],
      activeId: null,
      toast: { kind: 'info', text: 'Batch cleared.' }
    });
  }
}

export const store = new Store();

/** Convenience: find an image by id. */
export function findImage(images: ProductImage[], id: string | null): ProductImage | null {
  return images.find((image) => image.id === id) ?? null;
}
